import { useEffect, useRef } from 'react';
import { findTargetIframes, findPhotoImg, measureFrame, applyLiveFraming } from './framingLive';
import { normalizeFraming, panFocus, panFocusAutoZoom, zoomBy, FRAMING_DEFAULT } from './framing';

// GESTES DE CADRAGE sur une photo selectionnee (phase 4a) : glisser pour choisir
// la partie visible, molette ou pincement pour zoomer, double-clic pour
// recentrer. Pointer events : meme code pour la souris, le stylet et le doigt.
//
// Pendant le geste, seul le rendu est modifie (framingLive.js) ; le cadrage est
// ENREGISTRE (onCommit) au relachement — un geste = un pas d'historique.
//
// `enabled` : seulement en mode "Remplir" (en mode "Entiere" il n'y a rien a
// deplacer). `framing` est relu a chaque geste (toujours le dernier).
const WHEEL_SENSITIVITY = 0.0018;
const WHEEL_COMMIT_DELAY_MS = 220;

export function useFramingGestures({ enabled, elementRef, target, itemUrls, occurrence = 0, fallbackRatio, framing, onCommit }) {
  const latest = useRef({});
  latest.current = { target, itemUrls, occurrence, fallbackRatio, framing, onCommit };

  useEffect(() => {
    const element = elementRef.current;
    if (!enabled || !element) return undefined;

    const pointers = new Map();
    let session = null;
    let wheelTimer = null;

    const open = () => {
      const { target: tgt, itemUrls: urls, occurrence: occ, fallbackRatio: ratio, framing: current } = latest.current;
      // La page visee, et pour une double page sa jumelle : les deux moities de
      // la photo bougent ensemble pendant le geste.
      const found = findTargetIframes(tgt)
        .map((frame) => ({ frame, img: findPhotoImg(frame, urls, occ) }))
        .filter((entry) => entry.img);
      if (!found.length) return null;
      const { frame: iframe, img } = found[0];
      const metrics = measureFrame(iframe, img, ratio);
      if (!metrics.imageRatio) return null;
      const base = normalizeFraming(current);
      return { img, imgs: found.map((entry) => entry.img), spread: Boolean(tgt && tgt.spread), ...metrics, base, current: base, changed: false };
    };
    const applyLive = (open, framing) => open.imgs.forEach((image) => applyLiveFraming(image, framing));

    const centroid = () => {
      const points = [...pointers.values()];
      return {
        x: points.reduce((sum, p) => sum + p.x, 0) / points.length,
        y: points.reduce((sum, p) => sum + p.y, 0) / points.length
      };
    };
    const spread = () => {
      const [a, b] = [...pointers.values()];
      return Math.hypot(a.x - b.x, a.y - b.y) || 1;
    };

    const commit = () => {
      if (session && session.changed) latest.current.onCommit(session.current);
    };

    const rebase = () => {
      if (!session) return;
      session.base = session.current;
      session.origin = centroid();
      session.startSpread = pointers.size >= 2 ? spread() : null;
    };

    const onPointerDown = (event) => {
      if (event.pointerType === 'mouse' && event.button !== 0) return;
      // Les boutons de la barre, du texte, etc. gardent leur comportement.
      if (event.target.closest('.atelier-framing-bar, button, input, textarea, form, a')) return;
      if (!session) { session = open(); }
      if (!session) return;
      pointers.set(event.pointerId, { x: event.clientX, y: event.clientY });
      try { element.setPointerCapture(event.pointerId); } catch (_error) { /* pointeur deja libere */ }
      rebase();
      element.classList.add('is-framing-drag');
    };

    const onPointerMove = (event) => {
      if (!session || !pointers.has(event.pointerId)) return;
      pointers.set(event.pointerId, { x: event.clientX, y: event.clientY });
      event.preventDefault();
      let next = session.base;
      if (pointers.size >= 2 && session.startSpread && session.base.fit === 'cover') {
        next = { ...session.base, zoom: zoomBy(session.base.zoom, spread() / session.startSpread) };
      } else {
        const point = centroid();
        // Double page : decaler la photo entiere, quitte a zoomer juste ce qu'il faut.
        const pan = session.spread && session.base.fit === 'cover' ? panFocusAutoZoom : panFocus;
        const focus = pan({
          focusX: session.base.focusX,
          focusY: session.base.focusY,
          dxPx: (point.x - session.origin.x) / session.scale,
          dyPx: (point.y - session.origin.y) / session.scale,
          frameW: session.frameW,
          frameH: session.frameH,
          imageRatio: session.imageRatio,
          zoom: session.base.zoom,
          fit: session.base.fit,
          ...(session.spread && session.base.fit === 'cover' ? {
            // Le point saisi (en pixels du cadre) : c'est lui qui suit le pointeur.
            anchorX: (session.origin.x - session.frameLeftPx) / session.scale,
            anchorY: (session.origin.y - session.frameTopPx) / session.scale
          } : {})
        });
        next = { ...session.base, ...focus };
      }
      session.current = next;
      session.changed = true;
      applyLive(session, next);
    };

    const onPointerEnd = (event) => {
      if (!pointers.has(event.pointerId)) return;
      pointers.delete(event.pointerId);
      try { element.releasePointerCapture(event.pointerId); } catch (_error) { /* deja libere */ }
      if (pointers.size === 0) {
        commit();
        session = null;
        element.classList.remove('is-framing-drag');
      } else {
        rebase(); // un doigt reste : on continue en glissement simple
      }
    };

    const onWheel = (event) => {
      event.preventDefault();
      if (!session) session = open();
      if (!session) return;
      if (session.base.fit === 'contain') { if (pointers.size === 0) session = null; return; } // "Entiere" : pas de zoom
      const zoom = zoomBy(session.current.zoom, Math.exp(-event.deltaY * WHEEL_SENSITIVITY));
      session.current = { ...session.current, zoom };
      session.changed = true;
      applyLive(session, session.current);
      clearTimeout(wheelTimer);
      wheelTimer = setTimeout(() => {
        commit();
        if (pointers.size === 0) session = null;
      }, WHEEL_COMMIT_DELAY_MS);
    };

    const onDoubleClick = (event) => {
      if (event.target.closest('.atelier-framing-bar, button, input, textarea, form, a')) return;
      const opened = open();
      if (!opened) return;
      applyLive(opened, FRAMING_DEFAULT);
      latest.current.onCommit({ ...opened.base, focusX: 50, focusY: 50, zoom: 1 });
    };

    element.addEventListener('pointerdown', onPointerDown);
    element.addEventListener('pointermove', onPointerMove);
    element.addEventListener('pointerup', onPointerEnd);
    element.addEventListener('pointercancel', onPointerEnd);
    element.addEventListener('wheel', onWheel, { passive: false });
    element.addEventListener('dblclick', onDoubleClick);
    return () => {
      clearTimeout(wheelTimer);
      element.removeEventListener('pointerdown', onPointerDown);
      element.removeEventListener('pointermove', onPointerMove);
      element.removeEventListener('pointerup', onPointerEnd);
      element.removeEventListener('pointercancel', onPointerEnd);
      element.removeEventListener('wheel', onWheel);
      element.removeEventListener('dblclick', onDoubleClick);
      element.classList.remove('is-framing-drag');
    };
  }, [enabled, elementRef]);
}
